import { RouteComponentProps } from 'react-router'
import { parseUrl } from 'query-string'
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  pipe,
  path,
  propEq,
  find,
  head,
  map,
  values,
  keys,
  reduce,
  filter,
  isEmpty,
  pathOr,
  prop,
  flatten,
  uniq,
  includes,
  all,
  equals,
  anyPass,
  omit,
  over,
  lensProp,
  identity,
  ifElse,
  always
} from 'ramda'
import { useToast } from '@machobear/design-system'
import { v4 as uuidv4 } from 'uuid'
import PageTitle from '@/components/page-title'
import { select } from '@/store'
import { show as showReviewTermsModal } from '@/pages/Application/ContractTypes/review-terms-modal'
import { Button } from '@/components/button'
import useCurrentSession from '@/hooks/use-current-session'
import Header from './custom-contract-header'
import { DefaultButtonBar } from './custom-contract-button-bar'
import AddDocuments from './add-documents'
import AddSigner from './add-signer'
import LeaseTerms from './lease-terms'
import DocumentView from './document-view'
import Context from './context'
import { getFormattedPackageFiles, getFormattedSigners, displayExistingItems } from './utils'
import showDocumentsIncompleteModal from './incomplete-modal'
import showProgressLostModal from './progress-modal'
import showUploadingModal from './uploading-modal'
import showUploadFailedModal from './upload-failed-modal'

type RouteParams = {
  listingId: string
  contractId: string
}

type KonvaCoordinates = {
  x: number
  y: number
  width: number
  height: number
  color: string
}

type ItemMetadata = {
  konva_id: string
  konva_coordinates: KonvaCoordinates
  konva_display_text: string
  konva_display_text_size: number
  konva_pdf_text_size: number
}

type DateSignedDetails = {
  signer_id: string
  format: 'Y-m-d'
}

type StandardDetails = {
  signer_id: string
}

type Item = {
  itemId: string
  page: string
  x: number
  y: number
  width: number
  height: number
  type: string
  metadata: ItemMetadata
  details: DateSignedDetails | StandardDetails
}

type RawItemData = {
  id: string
  itemId: string
  name: string
  page: string
  x: number
  y: number
  width: number
  height: number
  type: string
  metadata: any
  stroke: string
  text: string
  textSize: number
}

type ItemsArrayByPage = {
  [key: string]: Item[]
}

type UpdateMetadataHandler = (params: {
  packageFileId: string
  contractId: string
  metadata: {
    fields_metadata: {
      pages: ItemsArrayByPage
    }
  }
}) => Promise<{ response: Response; body: { message: string } }>

const formatLeaseTerms = pipe(
  omit(['isFormDirty']),
  over(lensProp('end_date'), ifElse(equals(''), always(null), identity))
)

const nonEditType = ['initials', 'signature', 'dateSigned']

const CustomContractCreate: React.FC<
  RouteComponentProps<RouteParams> & { sidebarExpanded: boolean }
> = ({
  match: {
    params: { contractId }
  },
  history,
  sidebarExpanded
}) => {
  let {
    location: { state }
  } = history
  if (state === undefined) state = {}

  const dispatch = useDispatch()
  const [signeeMetadata, setSigneeMetadata] = useState<any>()
  const [isLoading, setIsLoading] = useState(false)
  const session = useCurrentSession()
  const showToast = useToast()
  const { contract = {}, packageFiles = [] } = useSelector(state =>
    (select.contract.contractPackageInfo as any)(state, { contractId })
  )
  const { query } = parseUrl(history?.location?.search)
  const { currentDocument } = query as { currentDocument: string }
  const isExternal = !!session?.signer
  const userId = session?.signer ? session?.signer?.id : session?.id
  const { t } = useTranslation()
  const { metadata } = state as { metadata: {} }
  const [currentStep, setCurrentStep] = useState('documents')
  const [user, setUser] = useState()
  const [items, setItems] = useState<any>({})
  const [signedItems, setSignedItems] = useState<any>({})
  const [fieldsCounter, setFieldsCounter] = useState([])
  const [isFilled, setIsFilled] = useState(false)
  const [fileMetadata, setFileMetadata] = useState<any>(metadata)
  const [signees, setSignees] = useState<any>()
  const [pages, setPages] = useState<any>()
  const [selectedPage, setSelectedPage] = useState<any>(0)
  const [bottomNavSelectedItem, setBottomNavSelectedItem] = useState({})

  const [selectedShape, selectShape] = useState(null)
  const [leaseTerms, setLeaseTerms] = useState<any>({})
  const callback = useCallback(() => history.goBack(), [history])
  const isStateEmpty = Object.keys(state as any).length === 0
  const isEdit = !!currentDocument
  const isInitialPage = currentStep === 'documents' || (isEdit && currentStep === 'buildDocuments')

  const hasAgreedToCustomContractTerms = contract?.contract_terms_accepted
  const noContract = !contract || !contract.id
  const listingId = contract.listingId

  useEffect(() => {
    if (!noContract && !hasAgreedToCustomContractTerms) {
      showReviewTermsModal({
        listingId,
        history: history,
        onBack: callback
      })
    }
  }, [noContract, hasAgreedToCustomContractTerms, listingId, contractId, history, callback])

  useEffect(() => {
    if (isEdit) {
      setCurrentStep('buildDocuments')
    }
  }, [])

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      const response = await dispatch.contract.getPackage({ contractId: contractId })
      const tenancyAgreement = pipe(find(propEq('document_type', 'tenancy_agreement')))(
        response?.body?.package_files
      )

      const currentSigner = (pipe(values, head)(response?.body?.signers) as any)?.find(
        (object: { user_id: string }) => {
          return object.user_id === userId
        }
      )

      const formattedFiles = getFormattedPackageFiles(response?.body?.package_files)

      const formattedSigners = getFormattedSigners(response?.body?.signers)

      const existingItems = pipe(
        reduce((acc, file: any) => {
          const currentSignees = signees?.[file?.id]
          const existingItems = displayExistingItems(file?.fields_metadata?.pages, currentSignees)

          return {
            ...acc,
            [file?.id]: existingItems
          }
        }, {})
      )(formattedFiles)

      const defaultPages = pipe(
        keys,
        reduce(
          (acc, key) => ({
            ...acc,
            [key]: {}
          }),
          {}
        )
      )(response?.body?.signers || {})

      const activeDocument = isEdit ? currentDocument : (tenancyAgreement as any)?.id

      setSignees(formattedSigners)
      setItems(existingItems)
      setPages(defaultPages)
      setSigneeMetadata({
        contract_id: contractId,
        current_document: activeDocument,
        package_files: formattedFiles,
        signer: response?.body?.signers,
        current_signer_id: currentSigner?.id
      })
      setIsLoading(false)
    }

    if (isStateEmpty && !isExternal) fetchData()
  }, [contractId, dispatch.contract, userId, isStateEmpty, isExternal])

  useEffect(() => {
    if (isStateEmpty) {
      setFileMetadata(signeeMetadata)
    }
  }, [isStateEmpty, signeeMetadata])

  useEffect(() => {
    if (fileMetadata) setIsLoading(false)
  }, [fileMetadata])

  const handleValidate = (e: boolean | ((prevState: boolean) => boolean)) => {
    setIsFilled(e)
  }

  const currentLabel = [
    t('l.contract_edit.application_contract_edit.documents.button', 'Documents'),
    t('l.contract_edit.application_contract_edit.next_lease_terms.button', 'Next: Lease Terms'),
    t('l.contract_edit.application_contract_edit.next_recipients.button', 'Next: Recipients'),
    t('l.contract_edit.application_contract_edit.build_documents.button', 'Next: Build Documents'),
    t('l.contract_edit.application_contract_edit.save_and_preview.button', 'Save and Preview'),
    t('l.contract_edit.application_contract_edit.create_contract.button', 'Create Contract')
  ]

  const handleData = async () => {
    switch (currentStep) {
      case 'leaseTerms':
        if (leaseTerms.isFormDirty) {
          await dispatch.contract.updateLeaseTerms({
            terms: formatLeaseTerms(leaseTerms),
            contractId: contract?.id
          })
        }

        break
      case 'recipients':
        setIsLoading(true)
        dispatch.loading.setLoading()
        const response = await dispatch.contract.getPackage({ contractId: contractId })

        const formattedFiles = getFormattedPackageFiles(response?.body?.package_files)
        const formattedSigners = getFormattedSigners(response?.body?.signers)
        const defaultPages = pipe(
          keys,
          reduce(
            (acc, key) => ({
              ...acc,
              [key]: {}
            }),
            {}
          )
        )(response?.body?.signers || {})
        setFileMetadata({
          ...fileMetadata,
          package_files: formattedFiles,
          signer: response?.body?.signers
        })
        setPages(defaultPages)
        setSignees(formattedSigners)
        setIsLoading(false)
        dispatch.loading.stopLoading()
        break
    }
  }

  const isUploading = anyPass([includes('pending'), includes('in_progress')])

  const handleValidateUploads = () => {
    const uploadStatuses = pipe(map(prop('upload_status')))(packageFiles)
    const hasError = includes('error', uploadStatuses)
    const hasUploading = isUploading(uploadStatuses as any)
    const allFilesUploaded = all(equals('success'))(uploadStatuses as any)

    if (hasUploading) {
      showUploadingModal({
        contractId
      })
      return
    }

    if (hasError) {
      showUploadFailedModal({
        callback
      })
      return
    }

    if (allFilesUploaded) setCurrentStep(contractFlowKeys[current + 1])
  }

  const handlePutMetadata = async (itemsData: RawItemData[], packageId: string) => {
    const itemsByPage = itemsData.reduce((acc: ItemsArrayByPage, item: RawItemData) => {
      const page = item.name.split('-')[1]

      if (!acc[page]) {
        acc[page] = []
      }
      const [userId, fieldName] = item.id.split('_')

      const updatedItem: Item = {
        itemId: uuidv4(),
        page,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        type: nonEditType.includes(fieldName) ? fieldName : 'text',
        metadata: {
          konva_id: `${item.id}-label`,
          konva_coordinates: {
            x: item.x,
            y: item.y,
            width: item.width,
            height: item.height,
            color: item.stroke
          },
          konva_display_text: item.text,
          konva_display_text_size: item.textSize,
          konva_pdf_text_size: item.textSize
        },
        details: {
          signer_id: signees?.[packageId][userId]?.['signer_id'],
          ...(fieldName === 'dateSigned' && { format: 'Y-m-d' })
        }
      }

      acc[page].push(updatedItem)
      return acc
    }, {})

    const useDraft = fileMetadata.package_files.find(
      (file: any) => file.id === packageId && file.state_machine === 'draft'
    )
    const { putFieldMetadata, putDraftFieldMetadata } = dispatch.contract
    const updateMetadaHandler: UpdateMetadataHandler = useDraft
      ? putDraftFieldMetadata
      : putFieldMetadata

    const { response, body } = await updateMetadaHandler({
      packageFileId: packageId,
      contractId: contractId,
      metadata: { fields_metadata: { pages: itemsByPage } }
    })

    if (response.ok) {
      setFileMetadata({ ...fileMetadata, current_document: packageId })
    } else {
      showToast({ message: body?.message, tone: 'critical' })
    }
  }

  const handleConfirm = async () => {
    const isSingleFileUpdate = !!currentDocument
    try {
      if (isSingleFileUpdate) {
        await handlePutMetadata(items[currentDocument], currentDocument)
      } else {
        await Promise.all(
          fileMetadata.package_files.map(({ id }: { id: string }) =>
            handlePutMetadata(items[id], id)
          )
        )
      }

      setCurrentStep(contractFlowKeys[current + 1])
    } catch (error: any) {
      showToast({ message: error, tone: 'critical' })
    }
  }

  const validateDocument = (file: any) => {
    const id = file?.id
    const signerValues = pipe(pathOr([], [id]), values)(signees as any)

    const requiredUsers = pipe(filter(propEq('recipient_signing_status', 'signature_required')))(
      signerValues as any
    )
    const fileSigners = pipe(map(prop('recipient_id')))(requiredUsers as any)
    let fileRecipients = pipe(map(prop('recipient_id')))(requiredUsers as any)

    pipe(
      pathOr([], [id]),
      map((item: any) => {
        const validType = item?.inputType === 'initials' || item?.inputType === 'signature'
        const recipientId = item?.recipient_id

        if ((fileSigners as any).includes(recipientId) && validType)
          fileRecipients = filter(u => u !== recipientId, fileRecipients as any)
      })
    )(items)

    return fileRecipients
  }

  const handleValidateDocuments = () => {
    const tenancyAgreementId: any = pipe(
      find(propEq('document_type', 'tenancy_agreement')) as any,
      prop('id')
    )(packageFiles)

    const signerValues = pipe(pathOr([], [tenancyAgreementId]), values)(signees as any)

    const requiredUsers = pipe(filter(propEq('recipient_signing_status', 'signature_required')))(
      signerValues as any
    )
    const fileSigners = pipe(map(prop('recipient_id')))(requiredUsers as any)
    let fileRecipients = pipe(map(prop('recipient_id')))(requiredUsers as any)

    pipe(
      values,
      flatten,
      map((item: any) => {
        const validType = item?.inputType === 'initials' || item?.inputType === 'signature'
        const recipientId = item?.recipient_id

        if ((fileSigners as any).includes(recipientId) && validType)
          fileRecipients = filter(u => u !== recipientId, fileRecipients as any)
      })
    )(items)

    const hasAllPagesFilled = isEmpty(fileRecipients)

    hasAllPagesFilled
      ? handleConfirm()
      : showDocumentsIncompleteModal({
          contractId,
          usersWithMissingItems: fileRecipients,
          handleConfirm
        })
  }

  const handleValidateEditDocument = () => {
    const currentFile = pipe(find(propEq('id', currentDocument)))(packageFiles)
    const usersWithMissingItems = pipe(validateDocument, flatten, uniq)(currentFile)

    const hasAllPagesFilled = isEmpty(usersWithMissingItems)

    hasAllPagesFilled
      ? handleConfirm()
      : showDocumentsIncompleteModal({
          contractId,
          usersWithMissingItems,
          handleConfirm
        })
  }

  const validateSignatures = isEdit ? handleValidateEditDocument : handleValidateDocuments

  const footerNav = (direction: string) => {
    window.scrollTo(0, 0)
    if (direction === 'next') {
      handleData()
      if (currentStep === 'buildDocuments') {
        validateSignatures()
        return
      }
      if (currentStep === 'documents') {
        handleValidateUploads()
        return
      }
      if (current + 1 !== 5) {
        setCurrentStep(contractFlowKeys[current + 1])
      }
    } else {
      if (contractFlowKeys[current] === 'upload') {
        history.goBack()
      }
      if (current - 1 !== -1) {
        setCurrentStep(contractFlowKeys[current - 1])
      }
    }
  }

  const handleNext = () => footerNav('next')
  const handleBack = (items: any) => {
    if (!isInitialPage) {
      setIsFilled(true)
      footerNav('previous')
    } else {
      if (values(items).length > 0) {
        showProgressLostModal({
          callback
        })
      } else {
        callback()
      }
    }
  }

  const contractFlow: Record<string, JSX.Element> = {
    documents: (
      <AddDocuments
        currentStep={currentStep}
        handleValidate={handleValidate}
        contractId={contractId}
        setIsFilled={setIsFilled}
        setSignees={setSignees}
      />
    ),
    leaseTerms: (
      <LeaseTerms
        currentStep={currentStep}
        handleValidate={handleValidate}
        contract={contract}
        setLeaseTerms={setLeaseTerms}
      />
    ),
    recipients: (
      <AddSigner
        handleValidate={handleValidate}
        contractId={contractId}
        signees={signees}
        setSignees={setSignees}
        items={items}
      />
    ),
    buildDocuments: (
      <DocumentView
        currentStep={currentStep}
        contractId={contractId}
        signees={signees}
        setSignees={setSignees}
        pages={pages}
        setPages={setPages}
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        isEdit={isEdit}
        handleBack={handleBack}
        setIsFilled={setIsFilled}
      />
    ),
    createContract: (
      <DocumentView
        currentStep={currentStep}
        contractId={contractId}
        signees={signees}
        setSignees={setSignees}
        pages={pages}
        setPages={setPages}
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        isEdit={isEdit}
        handleBack={handleBack}
        setIsFilled={setIsFilled}
      />
    )
  }
  const contractFlowKeys = Object.keys(contractFlow)
  const current = contractFlowKeys.indexOf(currentStep)

  const contractData: any = useSelector(path(['contract', contractId, 'contract']))
  const listingAddress = `${
    contractData?.units.unit_number ? `${contractData?.units.unit_number} - ` : ''
  }${contractData?.buildings?.full_address}`

  if (isLoading) return <></>

  return (
    <Context.Provider
      value={{
        user,
        setUser,
        items,
        setItems,
        fieldsCounter,
        setFieldsCounter,
        fileMetadata,
        setFileMetadata,
        signedItems,
        setSignedItems,
        contract,
        bottomNavSelectedItem,
        selectedShape,
        selectShape
      }}
    >
      <PageTitle to={`/listing/${listingId}`}>{listingAddress}</PageTitle>

      <Header currentStep={currentStep} contract={contract} isEdit={isEdit} />

      {contractFlow[currentStep]}
      {currentStep !== 'createContract' && (
        <DefaultButtonBar
          handleBack={() => handleBack(items)}
          isSigning={false}
          isInitial={isInitialPage}
          sidebarExpanded={sidebarExpanded}
          sidebar={true}
        >
          <Button
            theme="primary"
            size="small"
            type="submit"
            disabled={!isFilled}
            onClick={handleNext}
          >
            {currentLabel[current + 1]}
          </Button>
        </DefaultButtonBar>
      )}
    </Context.Provider>
  )
}

export default CustomContractCreate
